import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-responsive-modal';
import CloseModal from '../svg/CloseModal';
import { I18n } from 'react-redux-i18n';
import CallApi from '../../../utils/CallApi';
import '../sass/customerAccount.css';
import CustomSwitch from '../../../components/Button/CustomSwitch';
import { Input, Button, Text } from '@capps/laddition-storybook';
import { Grid, Container } from '@material-ui/core';
import ImgInfo from '../img/ info.png';
import insertVarInTxt from '../../../utils/InsertVarInText';

function ModalSettings({ openModal, onClose, onSuccess, onError, isSuite }) {
	const [loading, setLoading] = useState(false);
	const [switchStates, setSwitchStates] = useState({});
	const [accountData, setAccountData] = useState({
		bic: '',
		iban: '',
		ticket_sending_msg: '',
	});
	const [modifiedFields, setModifiedFields] = useState({});
	const [isFocused, setIsFocused] = useState(false);
	const inputRef = useRef(null);

	const settingsOptions = [
		{
			label: 'customerAccount.customerAccountManagement',
			dbKey: 'customer_account',
			type: 'switch',
		},
		{
			label: 'customerAccount.deactivationOnBalanceOverrun',
			dbKey: 'ca_auto_disable',
			type: 'switch',
		},
		{
			label: 'customerAccount.preventPaying',
			dbKey: 'ca_no_up_amount',
			type: 'switch',
		},
		{
			label: 'customerAccount.preventNegativeBalance',
			dbKey: 'ca_no_negative',
			type: 'switch',
		},
		{
			label: 'customerAccount.preventPaidTickets',
			dbKey: 'ca_no_us_ticket',
			type: 'switch',
		},
		{
			label: 'customerAccount.preventCountingCustomers',
			dbKey: 'ca_auto_disabled',
			type: 'switch',
		},
		{
			label: 'customerAccount.autoEmailNewTicket',
			dbKey: 'ca_auto_email_new_tk',
			type: 'switch',
		},
		{
			label: 'customerAccount.preventPayment',
			dbKey: 'ca_floor_exc_payment',
			type: 'switch',
		},
		{ label: 'customerAccount.customerBic', dbKey: 'bic', type: 'input' },
		{ label: 'customerAccount.customerIban', dbKey: 'iban', type: 'input' },
		{ label: 'customerAccount.alertEmail', dbKey: 'ca_alert_email', type: 'input' },
		{ label: 'customerAccount.alertBalance', dbKey: 'ca_alert_default_balance', type: 'input' },
		{ label: 'customerAccount.ticketSendingMsg', dbKey: 'ticket_sending_msg', type: 'input' },
	];

	const variablesServer = [
		{ label: 'customerAccount.ticketSendingMsg',
			dbKey: 'ticket_sending_msg',
			type: 'input'
		},
		{ label: 'customerAccount.displayKeyFigures',
			dbKey: 'ca_display_key_figures',
			type: 'switch'
		},
	]

	const msgFields = [
		{ label: I18n.t('customerAccount.firstname'), key: "customerFirstName" },
		{ label: I18n.t('customerAccount.name'), key: "customerLastName" },
		{ label: I18n.t('customerAccount.companyName'), key: "company" },
	];

	const fetchUserData = useCallback(async (endpoint, items) => {
		try {
			const keys = items.map(item => item.dbKey);

			return await CallApi(
				'GET',
				`customer-account/${endpoint}`,
				'',
				{ keys }
			);
		} catch (error) {
			console.error(`Error retrieving user data from ${endpoint}:`, error);
		}
	}, []);

	const upsertUserData = useCallback(async (endpoint, key, value) => {
		try {
			await CallApi(
				'PUT',
				`customer-account/${endpoint}`,
				'',
				{ key, value }
			);
		} catch (error) {
			console.error('Error upserting user data:', error);
		}
	}, []);

	useEffect(() => {
		const loadUserOptions = async () => {
			setLoading(true);
			try {
				const dataRes = await fetchUserData('getOptions', settingsOptions);
				const serverDataRes = await fetchUserData('getVariablesServer', variablesServer);

				if (dataRes && dataRes.length) {
					const data = dataRes[0];
					const serverData = serverDataRes[0] ?? {};
					const initialSwitchStates = settingsOptions.reduce((acc, option) => {
						acc[option.label] = data[option.dbKey] === '1';
						return acc;
					}, {});
					// variable_server switches default to enabled (e.g. key figures displayed by default)
					variablesServer
						.filter(option => option.type === 'switch')
						.forEach(option => {
							initialSwitchStates[option.label] =
								(serverData[option.dbKey] ?? '1') !== '0';
						});
					setSwitchStates(initialSwitchStates);
					setAccountData({
						bic: data.bic,
						iban: data.iban,
						ca_alert_email: data.ca_alert_email,
						ca_alert_default_balance: data.ca_alert_default_balance,
						ticket_sending_msg: serverData.ticket_sending_msg ?? '',
					});
				}
			} catch (error) {
				console.error('Error loading user options:', error);
			} finally {
				setLoading(false);
			}
		};

		if (openModal) {
			loadUserOptions();
		}
	}, [openModal]);

	const updateField = (key, value) => {
		setAccountData(prev => ({
			...prev,
			[key]: value,
		}));

		setModifiedFields(prev => ({
			...prev,
			[key]: value,
		}));
	};

	const handleChangeSwitch = (event, option) => {
		const isChecked = event.target.checked;
		setSwitchStates(prevState => ({
			...prevState,
			[option.label]: isChecked,
		}));

		setModifiedFields(prevModified => ({
			...prevModified,
			[option.dbKey]: isChecked ? '1' : '0',
		}));
	};

	const handleChangeInput = event => {
		const { name, value } = event.target;

		let newValue = value;

		if (name === "ca_alert_default_balance") {
			const cleanedValue = value.replace(",", ".").trim();
	
			if (cleanedValue === "") {
				newValue = 0;
			} else {
				const parsed = parseFloat(cleanedValue);
				if (isNaN(parsed)) {
					newValue = 0;
				} else {
					newValue = parsed <= 0 ? parsed : -parsed;
				}
			}
		}

		updateField(name, newValue)
	};

	const handleSubmit = async () => {
		try {
			setLoading(true);
			const variableServerKeys = variablesServer.map(v => v.dbKey);
			for (const [key, value] of Object.entries(modifiedFields)) {
				if (variableServerKeys.includes(key)) {
					await upsertUserData('upsertVariableServer', key, value);
				} else {
					await upsertUserData('upsertOption', key, value);
				}
			}
			onSuccess();
			setModifiedFields({});
		} catch (error) {
			console.error('Erreur lors de la sauvegarde des paramètres:', error);
			onError();
		} finally {
			setLoading(false);
			onClose();
		}
	};

	return (
		<Modal
			classNames={{
				modal: 'export_history_modal',
				overlay: 'modal_export_history_overlay',
			}}
			open={openModal}
			showCloseIcon={false}
			closeOnOverlayClick={true}
			onClose={() => {
				if (loading) return false;
				onClose();
			}}>
			<div className="export_modal_content">
				<div
					className="close_icon"
					onClick={e => {
						if (loading) return false;
						onClose();
					}}>
					<CloseModal />
				</div>
				<h3>Paramètres</h3>
				<div className="settings_modal_main">
					<Container maxWidth="lg">
						<div>
							{settingsOptions
								.filter(option =>
									option.dbKey !== 'ca_floor_exc_payment' || isSuite
								)
								.map(option =>
									option.type === 'switch' && (
										<div className="modal-row p-2 items-center" key={option.label}>
											<p className="modal-text">{I18n.t(option.label)}</p>
											<CustomSwitch
												checked={switchStates[option.label] || false}
												onChange={e => handleChangeSwitch(e, option)}
												inputProps={{ 'aria-label': 'controlled' }}
											/>
										</div>
									)
								)}
						</div>
						<div>
							{variablesServer
								.filter(option => option.type === 'switch')
								.map(option => (
									<div className="modal-row p-2 items-center" key={option.label}>
										<p className="modal-text">{I18n.t(option.label)}</p>
										<CustomSwitch
											checked={switchStates[option.label] ?? true}
											onChange={e => handleChangeSwitch(e, option)}
											inputProps={{ 'aria-label': 'controlled' }}
										/>
									</div>
								))}
						</div>
						<div className="modal-row p-2 mb-2 flex flex-col items-start">
							<p className="modal-text mb-2">{I18n.t('customerAccount.alertEmailTitle')}</p>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<Input
										label={I18n.t('customerAccount.alertEmail')}
										onChange={handleChangeInput}
										placeholder={I18n.t('customerAccount.alertEmail')}
										value={accountData.ca_alert_email}
										name="ca_alert_email"
										isValid={accountData.ca_alert_email ? true : false}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Input
										label={I18n.t('customerAccount.alertBalance')}
										onChange={handleChangeInput}
										placeholder={I18n.t('customerAccount.alertBalance')}
										value={accountData.ca_alert_default_balance ?? 0}
										name="ca_alert_default_balance"
										onKeyDown={(e) => {
											if (["+", "-", " "].includes(e.key)) {
												e.preventDefault();
											}
										}}
										isValid={accountData.ca_alert_default_balance ? true : false}
									/>
								</Grid>
							</Grid>
						</div>
						<div className="modal-row p-2 mb-2 flex flex-col items-start">
							<p className="modal-text mb-2">{I18n.t('customerAccount.customerBicAndIbanTitle')}</p>
							<div className="flex items-center mb-2 mt-2">
								<img src={ImgInfo} className="img-info" alt="info" />
								<Text
									className="text-form-modal my-0"
									color="main"
									variant="body">
									{I18n.t('customerAccount.bicAndIbanInfo')}
								</Text>
							</div>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<Input
										label={I18n.t('customerAccount.customerBic')}
										onChange={handleChangeInput}
										placeholder={I18n.t('customerAccount.customerBic')}
										value={accountData.bic}
										name="bic"
										isValid={accountData.bic ? true : false}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Input
										label={I18n.t('customerAccount.customerIban')}
										onChange={handleChangeInput}
										placeholder={I18n.t('customerAccount.customerIban')}
										value={accountData.iban}
										name="iban"
										isValid={accountData.iban ? true : false}
									/>
								</Grid>
							</Grid>
						</div>
						<div className="modal-row p-2 mb-2 flex flex-col items-start">
							<p className="modal-text mb-2">{I18n.t('customerAccount.ticketSendingMsgTitle')}</p>
							<textarea
								ref={inputRef}
								className="settings-input"
								value={accountData.ticket_sending_msg || ''}
								onChange={handleChangeInput}
								name="ticket_sending_msg"
								placeholder={I18n.t('customerAccount.defaultTicketSendingMsg')}
							/>
							<div className="flex flex-row gap-2 items-center">
								<p className="text-lg">{I18n.t('customerAccount.clickToInsert')}</p>
								{msgFields.map((el) => (
									<button
										key={el.key}
										className="field-btn text-lg"
										onMouseDown={(e) => {
											e.preventDefault();
											insertVarInTxt({
												input: inputRef.current,
												value: accountData.ticket_sending_msg || '',
												key: el.key,
												onChange: (newValue) =>
													updateField('ticket_sending_msg', newValue),
											})
										}}
									>
										{el.label}
									</button>
								))}
							</div>
						</div>
						<div className="btn-row">
							<Button
								color="secondary"
								label={I18n.t('accountMigration.btnValidate')}
								disabled={loading}
								onClick={handleSubmit}
							/>
						</div>
					</Container>
				</div>
			</div>
		</Modal>
	);
}

export default ModalSettings;
